import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { http, whatsappHref, formatPhoneDisplay } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, PencilSimple, Trash, WhatsappLogo, Check, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, inputCls } from "@/pages/PipelinePage";
import ImportContactsModal from "@/components/ImportContactsModal";

export const ALLOWED_SOURCES = [
  "Website",
  "Telemarketing",
  "Referral",
  "Repeat Order Website",
  "Repeat Order Referral",
  "Repeat Order Konsultan",
];

export default function ContactsPage() {
  const { t } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const { user: me } = useAuth();

  const load = async () => {
    const [c, u, s] = await Promise.all([
      http.get("/contacts"), http.get("/users"), http.get("/services")
    ]);
    setContacts(c.data);
    setUsers(u.data);
    setServices(s.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = contacts.filter((c) =>
    !query || [c.name, c.email, c.company, c.phone, ...(c.services || [])]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(query.toLowerCase()))
  );

  const del = async (id) => {
    if (!window.confirm("Hapus kontak ini?")) return;
    await http.delete(`/contacts/${id}`);
    toast.success("Kontak dihapus");
    load();
  };

  const userMap = users.reduce((a, u) => ({ ...a, [u.id]: u }), {});

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Contacts</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]">Kontak & Leads</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="contacts-search"
            placeholder={t("common.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 sm:flex-initial sm:w-52 px-3 py-2 bg-white rounded-lg border border-[#CBD5E1] text-xs sm:text-sm text-[#173042] placeholder:text-slate-400 focus:outline-none focus:border-[#173B4D] focus:ring-1 focus:ring-[#173B4D]"
          />
          <button
            onClick={() => setShowImport(true)}
            data-testid="import-contacts-btn"
            className="border border-[#CBD5E1] bg-white hover:bg-[#F8FAFC] text-[#173042] px-3.5 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold shadow-2xs transition-colors"
          >
            <FileXls size={16} weight="regular" className="text-[#2E8B57]" /> <span>Impor Excel</span>
          </button>
          <button
            data-testid="new-contact-btn"
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="bg-[#173B4D] hover:bg-[#112B38] text-white px-3.5 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold shadow-xs transition-colors"
          >
            <Plus size={16} weight="bold" /> <span>{t("action.newContact")}</span>
          </button>
        </div>
      </div>

      {/* Mobile Card List (< md) */}
      <div className="md:hidden space-y-3 mb-6" data-testid="contacts-mobile-list">
        {filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-sm text-[#64748B]">
            {t("empty.title")}
          </div>
        )}
        {filtered.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-2xs space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-sm text-[#173042]">{c.name}</div>
                {c.company && <div className="text-xs text-[#64748B] mt-0.5">{c.company}</div>}
                {c.email && <div className="text-[11px] font-mono-data text-[#64748B] mt-0.5">{c.email}</div>}
              </div>
              <span className="text-[9px] uppercase tracking-wider font-bold bg-[#F1F5F9] text-[#173042] px-2 py-0.5 rounded border border-[#E2E8F0]">
                {c.status || "lead"}
              </span>
            </div>

            {/* Phone & WhatsApp */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#F1F5F9]">
              <div>
                {c.phone ? (
                  <a
                    href={whatsappHref(c.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[#2E8B57] hover:text-[#25D366] font-mono-data text-xs font-semibold"
                  >
                    <WhatsappLogo size={15} weight="fill" />
                    <span>{formatPhoneDisplay(c.phone)}</span>
                  </a>
                ) : (
                  <span className="text-xs text-[#64748B]">-</span>
                )}
              </div>
              <div className="text-[11px] text-[#64748B]">
                Sales: <span className="font-semibold text-[#173042]">{userMap[c.owner_id]?.name || "-"}</span>
              </div>
            </div>

            {/* Services */}
            {(c.services || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {c.services.map((s) => (
                  <span key={s} className="text-[9px] uppercase font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded">
                    {s}
                  </span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1F5F9]">
              <button
                onClick={() => { setEditing(c); setShowModal(true); }}
                className="px-3 py-1.5 rounded-md border border-[#CBD5E1] text-xs font-semibold text-[#173042] hover:bg-[#F8FAFC] flex items-center gap-1"
              >
                <PencilSimple size={13} /> <span>Edit</span>
              </button>
              <button
                onClick={() => del(c.id)}
                className="px-3 py-1.5 rounded-md border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 flex items-center gap-1"
              >
                <Trash size={13} /> <span>Hapus</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table (>= md, scrollable container) */}
      <div className="hidden md:block bg-white rounded-xl border border-[#E2E8F0] overflow-x-auto shadow-2xs">
        <table className="w-full min-w-[850px]" data-testid="contacts-table">
          <thead>
            <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <Th>{t("common.name")}</Th>
              <Th>{t("common.company")}</Th>
              <Th>{t("common.phone")} · WhatsApp</Th>
              <Th>Layanan</Th>
              <Th>{t("common.status")}</Th>
              <Th>{t("common.owner")}</Th>
              <Th>{t("common.actions")}</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-[#64748B]">{t("empty.title")}</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors">
                <Td>
                  <div className="font-semibold text-xs sm:text-sm text-[#173042]">{c.name}</div>
                  {c.email && <div className="text-[11px] font-mono-data text-[#64748B]">{c.email}</div>}
                </Td>
                <Td>{c.company || "-"}</Td>
                <Td>
                  {c.phone ? (
                    <a
                      href={whatsappHref(c.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`wa-link-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-[#2E8B57] hover:text-[#25D366] font-mono-data text-xs font-semibold"
                      title="Buka WhatsApp"
                    >
                      <WhatsappLogo size={15} weight="fill" />
                      {formatPhoneDisplay(c.phone)}
                    </a>
                  ) : "-"}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {(c.services || []).length === 0 && <span className="text-xs text-[#64748B]">-</span>}
                    {(c.services || []).map((s) => (
                      <span key={s} className="text-[9px] uppercase tracking-[0.05em] font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded">
                        {s}
                      </span>
                    ))}
                  </div>
                </Td>
                <Td>
                  <span className="text-[9px] uppercase tracking-wider font-bold bg-[#F1F5F9] text-[#173042] px-2 py-0.5 rounded border border-[#E2E8F0]">
                    {c.status || "lead"}
                  </span>
                </Td>
                <Td><span className="text-xs font-medium text-[#173042]">{userMap[c.owner_id]?.name || "-"}</span></Td>
                <Td>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(c); setShowModal(true); }} className="text-[#64748B] hover:text-[#173042] p-1 rounded hover:bg-[#F1F5F9]" data-testid={`edit-contact-${c.id}`}>
                      <PencilSimple size={15} />
                    </button>
                    <button onClick={() => del(c.id)} className="text-[#64748B] hover:text-red-600 p-1 rounded hover:bg-red-50" data-testid={`delete-contact-${c.id}`}>
                      <Trash size={15} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <ContactModal
          users={users}
          services={services}
          initial={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
          onServicesChanged={async () => {
            const { data } = await http.get("/services");
            setServices(data);
          }}
        />
      )}
      {showImport && (
        <ImportContactsModal
          services={services}
          users={users}
          currentUser={me}
          onClose={() => setShowImport(false)}
          onImported={load}
          onServicesChanged={async () => {
            const { data } = await http.get("/services");
            setServices(data);
          }}
        />
      )}
    </AppLayout>
  );
}

function ContactModal({ onClose, onSaved, initial, users, services, onServicesChanged }) {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const canAddService = me?.role === "admin" || me?.role === "manager";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", position: "",
    status: "lead", source: "", notes: "", owner_id: "", services: [],
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const [newSvc, setNewSvc] = useState("");
  const [addingSvc, setAddingSvc] = useState(false);

  const toggleService = (name) => {
    setForm((f) => ({
      ...f,
      services: (f.services || []).includes(name)
        ? f.services.filter((s) => s !== name)
        : [...(f.services || []), name],
    }));
  };

  const addNewService = async () => {
    const name = newSvc.trim();
    if (!name) return;
    setAddingSvc(true);
    try {
      await http.post("/services", { name });
      toast.success(`Layanan "${name}" ditambahkan`);
      setNewSvc("");
      await onServicesChanged();
      setForm((f) => ({ ...f, services: [...new Set([...(f.services || []), name])] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menambah layanan");
    } finally {
      setAddingSvc(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      payload.name = form.name;
      payload.status = form.status;
      payload.services = form.services || [];
      if (initial) await http.patch(`/contacts/${initial.id}`, payload);
      else await http.post("/contacts", payload);
      toast.success(initial ? "Kontak diperbarui" : "Kontak dibuat");
      onSaved();
    } catch { toast.error("Gagal menyimpan kontak"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden page-enter" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] shrink-0">
          <div>
            <h3 className="font-bold text-sm sm:text-base text-[#173042]">{initial ? "Ubah Kontak" : "Kontak Baru"}</h3>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5">Kelola data klien dan prospek sertifikasi</p>
          </div>
          <button onClick={onClose} data-testid="contact-modal-close" className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#E2E8F0]"><X size={18} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden" data-testid="contact-form">
          <div className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label={t("common.name")} required>
                <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Nama Lengkap" />
              </Field>
              <Field label={t("common.company")}>
                <input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} placeholder="PT / CV..." />
              </Field>
              <Field label={t("common.email")}>
                <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="email@perusahaan.com" />
              </Field>
              <Field label={`${t("common.phone")} · WhatsApp`}>
                <input
                  data-testid="contact-phone"
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls}
                  placeholder="08xx atau +62xx"
                />
              </Field>
              <Field label="Jabatan">
                <input value={form.position || ""} onChange={(e) => setForm({ ...form, position: e.target.value })} className={inputCls} placeholder="Direktur / Manager" />
              </Field>
              <Field label={t("common.status")}>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                  <option value="lead">Lead</option>
                  <option value="customer">Customer</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Sumber">
                <select
                  value={form.source || ""}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className={inputCls}
                  data-testid="contact-source"
                >
                  <option value="">-- Pilih Sumber --</option>
                  {ALLOWED_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Assign ke">
                <select value={form.owner_id || ""} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} className={inputCls}>
                  <option value="">Saya</option>
                  {users.filter((u) => u.role !== "admin").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
            </div>

            {/* Services picker */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#64748B] mb-2">Layanan yang Ditawarkan</div>
              <div className="flex flex-wrap gap-1.5" data-testid="services-picker">
                {services.length === 0 && <div className="text-xs text-[#64748B]">Belum ada layanan.</div>}
                {services.map((s) => {
                  const active = (form.services || []).includes(s.name);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => toggleService(s.name)}
                      data-testid={`service-chip-${s.name}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] uppercase tracking-[0.05em] font-semibold rounded-md transition-colors border ${
                        active
                          ? "bg-[#173B4D] border-[#173B4D] text-white shadow-2xs"
                          : "border-[#CBD5E1] text-[#64748B] hover:border-[#173B4D] hover:text-[#173042]"
                      }`}
                      title={s.description || ""}
                    >
                      {active && <Check size={12} weight="bold" />}
                      {s.name}
                    </button>
                  );
                })}
              </div>

              {canAddService && (
                <div className="mt-2.5 flex gap-2 border-t border-dashed border-[#E2E8F0] pt-2.5">
                  <input
                    value={newSvc}
                    onChange={(e) => setNewSvc(e.target.value)}
                    data-testid="new-service-input"
                    placeholder="Tambah layanan baru (mis. ISO 14001)"
                    className="flex-1 rounded-lg border border-[#CBD5E1] px-3 py-1.5 text-xs text-[#173042] focus:outline-none focus:border-[#173B4D]"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewService(); } }}
                  />
                  <button
                    type="button"
                    onClick={addNewService}
                    disabled={addingSvc || !newSvc.trim()}
                    data-testid="add-service-btn"
                    className="rounded-lg border border-[#CBD5E1] bg-white text-[#173042] hover:bg-[#F8FAFC] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-40"
                  >
                    <Plus size={12} weight="bold" className="inline mr-1" /> Tambah
                  </button>
                </div>
              )}
            </div>

            <Field label={t("common.notes")}>
              <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " min-h-[70px]"} placeholder="Catatan tambahan..." />
            </Field>
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-2.5 shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#173042] hover:bg-[#F8FAFC]">{t("action.cancel")}</button>
            <button disabled={saving} type="submit" data-testid="contact-submit" className="bg-[#173B4D] hover:bg-[#112B38] text-white px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition-colors shadow-xs">
              {saving ? "..." : t("action.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left py-3 px-4 font-bold uppercase tracking-wider text-[10px] text-[#64748B]">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-3.5 px-4 text-xs text-[#173042] ${className}`}>{children}</td>;
